import React from 'react';
import {
  Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { PETCADEMY_SHADOW } from '../../utilities';
import { hasComparison, percentOf, pointsChange } from './engagementMath';
import { Relationship, RelationshipStats, statsFor } from './relationships';

interface RelationshipTableProps {
  loading: boolean;
  daysAgo: number;
  relationships: Relationship[];
  current?: RelationshipStats[];
  previous?: RelationshipStats[];
}

const COLUMNS = ['Relationship', 'Supported', 'Engaged', 'Engagement Rate', 'Change', 'Lessons', 'Messages', 'Surveys'];

// Static table — rows keep the design's order and never re-sort.
export default function RelationshipTable({
  loading, daysAgo, relationships, current, previous,
}: RelationshipTableProps) {
  return (
    <TableContainer component={Paper} elevation={PETCADEMY_SHADOW}>
      <Table>
        <TableHead>
          <TableRow>
            {COLUMNS.map((c) => <TableCell key={c} sx={{ whiteSpace: 'nowrap' }}>{c}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {relationships.map((r) => {
            const now = statsFor(current, r);
            const before = statsFor(previous, r);
            const rate = percentOf(now.engaged, now.supported);
            const change = pointsChange(rate, percentOf(before.engaged, before.supported), daysAgo);
            // Change is teal or red like the card deltas; all time's "—" stays neutral.
            let changeColor: string | undefined;
            if (hasComparison(daysAgo)) changeColor = change.startsWith('-') ? 'error.main' : 'secondary.main';
            const cells = [
              now.supported.toLocaleString(),
              now.engaged.toLocaleString(),
              `${rate}%`,
              change,
              now.lessons.toLocaleString(),
              now.messages.toLocaleString(),
              now.surveys.toLocaleString(),
            ];
            return (
              <TableRow key={r.label}>
                <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>{r.label}</TableCell>
                {cells.map((value, i) => (
                  <TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    sx={{ whiteSpace: 'nowrap', color: i === 3 ? changeColor : undefined }}
                  >
                    {loading ? <Skeleton /> : value}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
